import { Amplify, Auth, Hub } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AwsCognitoConfig } from '../Config/auth';

Amplify.configure({ Auth: AwsCognitoConfig });

// Check if OAuth is configured
const isOAuthEnabled = Boolean(process.env.REACT_APP_AUTH_DOMAIN);

// Linked provider from Cognito identities attribute
interface LinkedProvider {
  providerName: string;
  providerType: string;
  userId: string;
  dateCreated: number;
}

// Parse identities JSON from Cognito user attributes
function parseLinkedProviders(identities: string | undefined): LinkedProvider[] {
  if (!identities) return [];
  try {
    return JSON.parse(identities);
  } catch {
    return [];
  }
}

// Maps AWS Cognito error codes to user-friendly messages
function getAuthErrorMessage(error: unknown, defaultMessage: string): string {
  // Check for network errors
  if (!navigator.onLine) {
    return 'No internet connection. Please check your network and try again.';
  }

  if (error instanceof Error) {
    // Network-related errors
    if (error.name === 'NetworkError' || error.message.includes('Network')) {
      return 'Unable to connect. Please check your internet connection.';
    }
  }

  // AWS Cognito specific errors
  const cognitoError = error as { code?: string; message?: string };
  const code = cognitoError?.code;

  switch (code) {
    // Sign in errors
    case 'UserNotFoundException':
      return 'No account found with this email address.';
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotConfirmedException':
      return 'Please verify your email address before signing in.';

    // Sign up errors
    case 'UsernameExistsException':
      return 'An account with this email already exists.';
    case 'InvalidPasswordException':
      return 'Password does not meet requirements.';

    // Verification code errors
    case 'CodeMismatchException':
      return 'Invalid verification code. Please try again.';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new one.';

    // Rate limiting
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'TooManyRequestsException':
      return 'Too many requests. Please wait a moment and try again.';

    // Invalid input
    case 'InvalidParameterException':
      return 'Invalid input. Please check your information and try again.';

    default:
      return defaultMessage;
  }
}

// Check for OAuth error (from sessionStorage, set in index.tsx before Amplify cleans URL)
function getOAuthError(): { message: string | null; isLinkingSuccess: boolean } {
  const parseError = (decoded: string): { message: string | null; isLinkingSuccess: boolean } => {
    // Check for account linking success (not an error, but a success message)
    if (decoded.includes('linked successfully') || decoded.includes('Account linked')) {
      return {
        message: 'Your Google account has been linked! Click "Continue with Google" to sign in.',
        isLinkingSuccess: true,
      };
    }

    // Check for EMAIL_EXISTS error from pre-sign-up Lambda
    if (decoded.includes('EMAIL_EXISTS')) {
      return {
        message:
          'An account with this email already exists. Please sign in with your email and password.',
        isLinkingSuccess: false,
      };
    }

    // Check for "Please sign in with Google" message (native signup when Google account exists)
    if (decoded.includes('Please sign in with Google')) {
      return {
        message: 'An account with this email already exists. Please sign in with Google.',
        isLinkingSuccess: false,
      };
    }

    // Fallback for Cognito's native attribute error
    if (decoded.includes('Attribute cannot be updated')) {
      return {
        message:
          'An account with this email already exists. Please sign in with your email and password.',
        isLinkingSuccess: false,
      };
    }

    // Check for other PreSignUp failures
    if (decoded.includes('PreSignUp')) {
      return {
        message:
          'An account with this email already exists. Please sign in with your email and password.',
        isLinkingSuccess: false,
      };
    }

    // Return decoded error for other cases
    return { message: decoded, isLinkingSuccess: false };
  };

  // Check sessionStorage first (captured before Amplify cleaned URL)
  const storedError = sessionStorage.getItem('oauth_error');
  if (storedError) {
    sessionStorage.removeItem('oauth_error');
    const decoded = decodeURIComponent(storedError);
    return parseError(decoded);
  }

  // Fallback: check URL params (in case they weren't cleaned)
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error && errorDescription) {
    const decoded = decodeURIComponent(errorDescription);
    return parseError(decoded);
  }

  return { message: null, isLinkingSuccess: false };
}

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string;
  username: string;
  email: string;
  linkedProviders: LinkedProvider[];
  hasGoogleLinked: boolean;
  oauthError: string | null;
  isLinkingSuccess: boolean;
  clearOAuthError: () => void;
  signIn: (username: string, password: string, rememberMe?: boolean) => Promise<Result>;
  signInWithGoogle: () => Promise<Result>;
  signUp: (username: string, email: string, password: string) => Promise<Result>;
  confirmSignUp: (username: string, code: string) => Promise<Result>;
  resendConfirmationCode: (username: string) => Promise<Result>;
  forgotPassword: (username: string) => Promise<Result>;
  forgotPasswordSubmit: (username: string, code: string, newPassword: string) => Promise<Result>;
  signOut: () => Promise<Result>;
  isOAuthEnabled: boolean;
}

interface Result {
  success: boolean;
  message: string;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [linkedProviders, setLinkedProviders] = useState<LinkedProvider[]>([]);
  const [oauthError, setOAuthError] = useState<string | null>(null);
  const [isLinkingSuccess, setIsLinkingSuccess] = useState(false);

  const hasGoogleLinked = linkedProviders.some((p) => p.providerName === 'Google');

  const clearOAuthError = () => {
    setOAuthError(null);
    setIsLinkingSuccess(false);
    // Clean up URL params
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());
  };

  // Check for OAuth error on mount (from sessionStorage, set before Amplify cleaned URL)
  useEffect(() => {
    const { message, isLinkingSuccess: linkingSuccess } = getOAuthError();
    if (message) {
      setOAuthError(message);
      setIsLinkingSuccess(linkingSuccess);
    }
  }, []);

  useEffect(() => {
    const hubListenerCancelToken = Hub.listen('auth', ({ payload }) => {
      const { event, data } = payload;

      // Handle OAuth/federated sign-in errors
      if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
        const errorMessage = data?.message || data?.error || 'Sign in failed';

        // Check for account linking success
        if (
          errorMessage.includes('linked successfully') ||
          errorMessage.includes('Account linked')
        ) {
          setOAuthError(
            'Your Google account has been linked! Click "Continue with Google" to sign in.',
          );
          setIsLinkingSuccess(true);
        } else if (
          errorMessage.includes('EMAIL_EXISTS') ||
          errorMessage.includes('PreSignUp') ||
          errorMessage.includes('already exists')
        ) {
          setOAuthError(
            'An account with this email already exists. Please sign in with your email and password.',
          );
          setIsLinkingSuccess(false);
        } else {
          setOAuthError(errorMessage);
          setIsLinkingSuccess(false);
        }
      }

      if (event === 'autoSignIn' || event === 'signIn') {
        const user = payload.data;

        // For native sign-in, attributes are in user.attributes
        // For federated sign-in (Google OAuth), attributes are in the ID token payload
        const attributes = user?.attributes;
        const tokenPayload = user?.signInUserSession?.idToken?.payload;

        const sub = attributes?.sub || tokenPayload?.sub || '';
        const email = attributes?.email || tokenPayload?.email || '';

        // identities can be in attributes (as JSON string) or in token payload (as array)
        let identities: LinkedProvider[] = [];
        if (attributes?.identities) {
          identities = parseLinkedProviders(attributes.identities);
        } else if (tokenPayload?.identities) {
          identities = Array.isArray(tokenPayload.identities)
            ? tokenPayload.identities
            : parseLinkedProviders(tokenPayload.identities);
        }

        setUserId(sub);
        setUsername(user?.username || '');
        setEmail(email);
        setLinkedProviders(identities);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else if (event === 'autoSignIn_failure' || event === 'signIn_failure') {
        setUserId('');
        setUsername('');
        setEmail('');
        setLinkedProviders([]);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    });

    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then((result) => {
        // For native sign-in, attributes are in result.attributes
        // For federated sign-in (Google OAuth), attributes are in the ID token payload
        const attributes = result?.attributes;
        const tokenPayload = result?.signInUserSession?.idToken?.payload;

        const sub = attributes?.sub || tokenPayload?.sub || '';
        const email = attributes?.email || tokenPayload?.email || '';

        // identities can be in attributes (as JSON string) or in token payload (as array)
        let identities: LinkedProvider[] = [];
        if (attributes?.identities) {
          identities = parseLinkedProviders(attributes.identities);
        } else if (tokenPayload?.identities) {
          // Token payload has identities as an array already
          identities = Array.isArray(tokenPayload.identities)
            ? tokenPayload.identities
            : parseLinkedProviders(tokenPayload.identities);
        }

        setUserId(sub);
        setUsername(result?.username || '');
        setEmail(email);
        setLinkedProviders(identities);
        setIsAuthenticated(true);
        setIsLoading(false);
      })
      .catch(() => {
        setUserId('');
        setUsername('');
        setEmail('');
        setLinkedProviders([]);
        setIsAuthenticated(false);
        setIsLoading(false);
      });

    return () => {
      hubListenerCancelToken();
    };
  }, []);

  const signIn = async (username: string, password: string, rememberMe = true) => {
    try {
      // Configure storage based on rememberMe preference
      // localStorage persists across browser sessions, sessionStorage is cleared on browser close
      const storage = rememberMe ? localStorage : sessionStorage;
      Amplify.configure({
        Auth: {
          ...AwsCognitoConfig,
          storage,
        },
      });

      const result = await Auth.signIn(username, password);

      setUserId(result?.attributes?.sub || '');
      setUsername(result?.username || '');
      setEmail(result?.attributes?.email || '');
      setLinkedProviders(parseLinkedProviders(result?.attributes?.identities));
      setIsAuthenticated(true);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to sign in. Please try again.'),
      };
    }
  };

  const signInWithGoogle = async () => {
    if (!isOAuthEnabled) {
      return {
        success: false,
        message: 'Google sign-in is not configured.',
      };
    }

    try {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to sign in with Google. Please try again.'),
      };
    }
  };

  const signUp = async (username: string, email: string, password: string) => {
    try {
      const { user } = await Auth.signUp({
        username,
        password,
        autoSignIn: { enabled: true },
        attributes: {
          email,
        },
      });

      setUsername(user.getUsername());
      setEmail(email);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to create account. Please try again.'),
      };
    }
  };

  const confirmSignUp = async function confirmSignUp(username: string, code: string) {
    try {
      await Auth.confirmSignUp(username, code, { forceAliasCreation: false });

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to verify account. Please try again.'),
      };
    }
  };

  const resendConfirmationCode = async function resendConfirmationCode(username: string) {
    try {
      await Auth.resendSignUp(username);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to resend code. Please try again.'),
      };
    }
  };

  const forgotPassword = async function forgotPassword(username: string) {
    try {
      await Auth.forgotPassword(username);

      // assume User uses email address to reset password
      setEmail(username);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to send reset code. Please try again.'),
      };
    }
  };

  const forgotPasswordSubmit = async function forgotPasswordSubmit(
    username: string,
    code: string,
    newPassword: string,
  ) {
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to reset password. Please try again.'),
      };
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUserId('');
      setUsername('');
      setEmail('');
      setLinkedProviders([]);
      setIsAuthenticated(false);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: getAuthErrorMessage(error, 'Unable to sign out. Please try again.'),
      };
    }
  };

  return {
    isLoading,
    isAuthenticated,
    userId,
    username,
    email,
    linkedProviders,
    hasGoogleLinked,
    oauthError,
    isLinkingSuccess,
    clearOAuthError,
    signIn,
    signInWithGoogle,
    signUp,
    confirmSignUp,
    forgotPassword,
    forgotPasswordSubmit,
    signOut,
    resendConfirmationCode,
    isOAuthEnabled,
  };
};

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};
